import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { fmtIST, fmtISTDay, toISTInput, fromISTInput } from "@/lib/ist";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Edit2, Trash2, Loader2, Link2Off } from "lucide-react";
import { toast } from "sonner";

const blank = { starts_at: "", duration_minutes: 90, joining_link: "", seat_limit: 100, is_active: true };

function SessionDialog({ session, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(blank);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onOpenChange = (o) => {
    setOpen(o);
    if (o) setForm(session ? { ...session, starts_at: toISTInput(session.starts_at) } : blank);
  };

  const onSave = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      starts_at: fromISTInput(form.starts_at), duration_minutes: Number(form.duration_minutes),
      joining_link: form.joining_link, seat_limit: Number(form.seat_limit), is_active: form.is_active,
    };
    try {
      if (session) await api.put(`/admin/masterclass-sessions/${session.id}`, payload);
      else await api.post("/admin/masterclass-sessions", payload);
      toast.success(session ? "Session updated" : "Session added");
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{session ? "Edit Session" : "Add Session"}</DialogTitle></DialogHeader>
        <form onSubmit={onSave} className="space-y-4" data-testid="session-form">
          <div><Label>Date & time (IST) *</Label><Input type="datetime-local" required value={form.starts_at} onChange={update("starts_at")} className="mt-1" data-testid="session-input-starts-at" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Duration (min)</Label><Input type="number" min={15} value={form.duration_minutes} onChange={update("duration_minutes")} className="mt-1" data-testid="session-input-duration" /></div>
            <div><Label>Seat limit</Label><Input type="number" min={1} value={form.seat_limit} onChange={update("seat_limit")} className="mt-1" data-testid="session-input-seats" /></div>
          </div>
          <div><Label>Joining link (Zoom / Google Meet)</Label><Input type="url" placeholder="https://" value={form.joining_link} onChange={update("joining_link")} className="mt-1" data-testid="session-input-link" /></div>
          <label className="flex items-center gap-3 text-sm"><Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} data-testid="session-input-active" />Show on website</label>
          <button type="submit" disabled={busy} data-testid="session-form-submit" className="w-full inline-flex items-center justify-center gap-2 bg-navy text-white py-3 rounded-full font-semibold hover:bg-navy-deep transition disabled:opacity-60">
            {busy && <Loader2 size={16} className="animate-spin" />}{session ? "Update Session" : "Add Session"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SessionsTable({ sessions, onChange }) {
  const remove = async (s) => {
    if (!window.confirm("Delete this session?")) return;
    try {
      await api.delete(`/admin/masterclass-sessions/${s.id}`);
      toast.success("Session deleted");
      onChange();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    }
  };
  return (
    <div className="mt-6 bg-white border border-gray-200 rounded-2xl overflow-x-auto">
      <Table>
        <TableHeader><TableRow><TableHead>Date (IST)</TableHead><TableHead>Registered</TableHead><TableHead>Joining link</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
        <TableBody>
          {sessions.map((s) => {
            const past = new Date(s.starts_at) < new Date();
            return (
              <TableRow key={s.id} data-testid={`session-row-${s.id}`}>
                <TableCell className="font-medium">{fmtIST(s.starts_at, { year: "numeric" })}<div className="text-xs text-gray-500">{s.duration_minutes} min</div></TableCell>
                <TableCell>{s.registered} / {s.seat_limit}</TableCell>
                <TableCell className="max-w-[220px] truncate text-xs">{s.joining_link || <span className="inline-flex items-center gap-1 text-amber-600"><Link2Off size={12} /> Missing</span>}</TableCell>
                <TableCell><Badge variant={past || !s.is_active ? "secondary" : "default"}>{past ? "Finished" : s.is_active ? "Live" : "Hidden"}</Badge></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <SessionDialog session={s} onSaved={onChange} trigger={<button data-testid={`session-edit-${s.id}`} className="p-2 text-gray-500 hover:text-navy"><Edit2 size={15} /></button>} />
                  <button onClick={() => remove(s)} data-testid={`session-delete-${s.id}`} className="p-2 text-gray-500 hover:text-red-600"><Trash2 size={15} /></button>
                </TableCell>
              </TableRow>
            );
          })}
          {sessions.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-gray-500 py-8">No sessions yet.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
}

const EMAIL_LABELS = [["confirmation", "Confirm"], ["reminder_24h", "24h"], ["reminder_1h", "1h"]];
const EMAIL_STYLE = { sent: "bg-green-100 text-green-700", failed: "bg-red-100 text-red-700", sending: "bg-amber-100 text-amber-700", skipped: "bg-gray-100 text-gray-500" };

function EmailStatus({ emails = {} }) {
  return (
    <div className="flex gap-1 flex-wrap">
      {EMAIL_LABELS.map(([k, label]) => emails[k] && (
        <span key={k} title={emails[k]} className={`text-[10px] px-2 py-0.5 rounded-full ${EMAIL_STYLE[emails[k]]}`}>{label}: {emails[k]}</span>
      ))}
    </div>
  );
}

function RegistrationsTable({ registrations, sessions }) {
  const when = (id) => { const s = sessions.find((x) => x.id === id); return s ? fmtIST(s.starts_at, { year: "numeric" }) : "—"; };
  return (
    <div className="mt-6 bg-white border border-gray-200 rounded-2xl overflow-x-auto">
      <Table>
        <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Contact</TableHead><TableHead>Session</TableHead><TableHead>Business</TableHead><TableHead>Emails</TableHead><TableHead>Registered</TableHead></TableRow></TableHeader>
        <TableBody>
          {registrations.map((r) => (
            <TableRow key={r.id} data-testid={`masterclass-row-${r.id}`}>
              <TableCell className="font-medium">{r.name}<div className="text-xs text-gray-500">{r.city}</div></TableCell>
              <TableCell><div className="text-xs">{r.email}</div><div className="text-xs text-gray-500">{r.phone}</div></TableCell>
              <TableCell className="text-xs">{when(r.session_id)}</TableCell>
              <TableCell><div>{r.business_name || "—"}</div>{r.annual_revenue && <Badge variant="secondary" className="mt-1">{r.annual_revenue}</Badge>}<div className="text-xs text-gray-500 max-w-[220px] truncate">{r.biggest_challenge}</div></TableCell>
              <TableCell><EmailStatus emails={r.emails} /></TableCell>
              <TableCell className="text-xs text-gray-500">{fmtISTDay(r.created_at)}</TableCell>
            </TableRow>
          ))}
          {registrations.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-gray-500 py-8">No registrations yet.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
}

export const MasterclassAdmin = ({ sessions, registrations, onChange }) => (
  <div data-testid="admin-section-masterclass">
    <div className="flex items-center justify-between flex-wrap gap-4">
      <h1 className="text-3xl font-display font-semibold text-navy">Masterclass</h1>
      <SessionDialog onSaved={onChange} trigger={
        <button data-testid="session-add-btn" className="inline-flex items-center gap-2 bg-navy text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-navy-deep transition"><Plus size={15} /> Add Session</button>
      } />
    </div>
    <h2 className="mt-8 font-display text-xl text-navy">Sessions</h2>
    <p className="text-sm text-gray-500">Registrants get a confirmation email, then reminders 24 hours and 1 hour before. Add the joining link so it's included.</p>
    <SessionsTable sessions={sessions} onChange={onChange} />
    <h2 className="mt-10 font-display text-xl text-navy">Registrations</h2>
    <RegistrationsTable registrations={registrations} sessions={sessions} />
  </div>
);
